/*
  graphDataToBotech.js
*/

// Smallest denomination we want for serialized X and Y positions, in the form (1 / N).
const DECIMAL_ACCURACY = 100;

/**
 * Helper to round to some desired decimal places.
 */
function roundNum(n) {
  return Math.round(n * DECIMAL_ACCURACY) / DECIMAL_ACCURACY;
}

/**
 * Helper to clear empty strings, empty objects, and empty arrays, from an object.
 * So things that are pretty useless if they are defined.
 */
function clearEmptyValues(obj) {
  const next = {};
  for (let [key, value] of Object.entries(obj)) {
    if (value != null) {
      if (value.constructor === Object) {
        value = clearEmptyValues(value);  // Nested clear.
      }
      if (
        (typeof value === 'string' && value.length > 0)
        || (typeof value === 'number')
        || (typeof value === 'boolean')
        || (Array.isArray(value) && value.length > 0)
        || (value.constructor === Object && Object.keys(value).length > 0)
      ) {
        next[key] = value;
      }
    }
  }
  return next;
}

/**
 * Helper to clear undefined values from an object.
 */
function clearUndefined(obj) {
  const res = {};
  if (obj) {
    for (const [key, val] of Object.entries(obj)) {
      if (val !== undefined) {
        res[key] = val;
      }
    }
  }
  return res;
}

/**
 * Converts force-graph data to botech data.
 *
 * NOTE: Assumes that "data" is the mutated version of force-graph data
 * (foce graph mutates the data when it's first sent in), hence we store anything that
 * we don't want mutated in "_botech" objects.
 */
export default function graphDataToBotech(data) {

  if (!data) {
    return {};
  }

  // Define botech object with some undefined values,
  // purely to set the order in the resulting object.

  const botech = {
    metadata: undefined,
    runtime: undefined,
    resources: undefined,
    nodes: undefined,
    links: undefined,
    subroutines: undefined,
  };

  if (data._botech) {
    Object.assign(botech, data._botech);
  }

  // Get the average X and Y positions and base the node positions on it.

  if (data.nodes) {

    let midX = 0;
    let midY = 0;
    let midCount = 0;

    for (const node of data.nodes) {
      if (typeof node.x === 'number' && typeof node.y === 'number') {
        midX += node.x;
        midY += node.y;
        midCount += 1;
      }
    }

    if (midCount > 0) {
      midX /= midCount;
      midY /= midCount;
    }

    botech.nodes = data.nodes.map(node => clearUndefined({
      id: undefined,
      x: undefined,
      y: undefined,
      ...node._botech,
      id: node.id,
      x: typeof node.x === 'number' ? roundNum(node.x - midX) : undefined,
      y: typeof node.y === 'number' ? roundNum(node.y - midY) : undefined,
    }));

  }

  // Links are first checked for string IDs, as this is how the data is initally structured
  // until mutated by ForceGraph.

  if (data.links) {
    botech.links = data.links.map(link => clearUndefined({
      id: undefined,
      source: undefined,
      target: undefined,
      ...link._botech,
      id: link.id,
      source: typeof link.source === 'string' ? link.source : link.source.id,
      target: typeof link.target === 'string' ? link.target : link.target.id,
    }));
  }

  // Clean subroutine data.

  if (botech.subroutines) {
    botech.subroutines = botech.subroutines.map(obj => {
      obj = clearEmptyValues(obj);
      delete obj._id;
      return obj;
    });
    if (botech.subroutines.length === 0) {
      delete botech.subroutines;
    }
  }

  // Remove any undefined values.
  // These get removed when stringified but we do this just in case.

  return clearUndefined(botech);

}
